import React, { useEffect, useState } from 'react';
import { loadConfig, loadSettings, storeSettings } from '../../services/api';
import { newsletterEvents, userEvents } from '../../utils/analytics';
import Loader from '../../utils/Loader';
import './Preferences.css';

function Preferences() {
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const newsletterKeys = Object.keys(config?.newsletters || {});
  const subscribedCount = Object.keys(settings || {}).filter(key => settings[key]).length;

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const [configData, settingsData] = await Promise.all([
        loadConfig(),
        loadSettings()
      ]);
      setConfig(configData);
      setSettings(settingsData || {});
      userEvents.formSubmit('load_preferences', true);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      userEvents.error('load_preferences', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveMsg('');
      await storeSettings(settings);
      setSaveMsg('Preferences saved');
      newsletterEvents.updatePreferences(settings);
      userEvents.formSubmit('save_preferences', true);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMsg('Failed to save');
      userEvents.formSubmit('save_preferences', false);
      userEvents.error('save_preferences', error.message);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 2500);
    }
  };

  const handleFrequencyChange = (newsletterKey, frequency) => {
    setSettings(prev => {
      const next = { ...prev };
      if (next[newsletterKey]) {
        next[newsletterKey] = { ...next[newsletterKey], frequency };
        userEvents.buttonClick('frequency_change', 'preferences');
        newsletterEvents.updatePreferences({ [newsletterKey]: { frequency } });
      }
      return next;
    });
  };

  const handleCardClick = (key) => {
    setSettings(prev => {
      const next = { ...(prev || {}) };
      const currentVal = Boolean(next[key]);
      if (currentVal) {
        delete next[key];
        userEvents.buttonClick('newsletter_unsubscribe', 'preferences');
        newsletterEvents.unsubscribe('preferences_page');
      } else {
        next[key] = { frequency: '1x' };
        userEvents.buttonClick('newsletter_subscribe', 'preferences');
        newsletterEvents.subscribe('preferences_page');
      }
      return next;
    });
  };

  return (
    <div className="preferences component-card">
      {loading ? (
        <Loader size="large" text="Loading preferences..." />
      ) : (
        <>
          {/* Compact Header */}
          <div className="preferences-header">
            <div className="preferences-title-section">
              <h2>Preferences ({subscribedCount} of {newsletterKeys.length})</h2>
            </div>
            
            <div className="preferences-actions">
              <button 
                className="save-button btn-primary" 
                onClick={handleSave} 
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Preferences'}
              </button>
              {saveMsg && (
                <span className={`save-msg ${saveMsg.includes('Failed') ? 'error' : 'success'}`}>
                  {saveMsg}
                </span>
              )}
            </div>
          </div>

          {newsletterKeys.length === 0 ? (
            <div className="empty-state">
              <p>No newsletters available.</p>
            </div>
          ) : (
            /* Newsletter Cards Grid */
            <div className="newsletter-cards-container scrollable-compact">
              <div className="newsletter-cards-grid">
                {newsletterKeys.map((key) => {
                  const isSubscribed = Boolean(settings?.[key]);
                  const newsletter = config?.newsletters?.[key];
                  const currentFrequency = settings?.[key]?.frequency || '1x';
                  
                                     return (
                     <div 
                       key={key} 
                       className={`newsletter-card ${isSubscribed ? 'subscribed' : 'unsubscribed'}`}
                       onClick={() => handleCardClick(key)}
                     >
                                               {/* Newsletter Header */}
                        <div className="newsletter-card-header">
                          <div className="newsletter-title">
                            <h4>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                          </div>
                        </div>

                       {/* Newsletter Description */}
                       <div className="newsletter-description">
                         <p>{newsletter?.description || 'No description available'}</p>
                       </div>

                       {/* Frequency Selection */}
                       {isSubscribed && (
                         <div className="frequency-section">
                           <label className="frequency-label">Frequency (per week):</label>
                           <div className="frequency-options">
                             {Object.entries(config?.frequencies || {}).map(([freqKey, freqData]) => (
                               <div
                                 key={freqKey}
                                 className={`frequency-option ${currentFrequency === freqKey ? 'selected' : ''}`}
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   handleFrequencyChange(key, freqKey);
                                 }}
                               >
                                 <span className="frequency-key">{freqKey}</span>
                               </div>
                             ))}
                           </div>
                         </div>
                       )}

                       {/* Status */}
                       <div className="newsletter-status">
                         <span className={`status-badge ${isSubscribed ? 'subscribed' : 'unsubscribed'}`}>
                           {isSubscribed ? 'Subscribed' : 'Not subscribed'}
                         </span>
                       </div>
                     </div>
                   );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Preferences;
